import { ResourceLinkCard } from "@/components/cards/resource-link-card";
import { Can } from "@/components/citadel";
import { useProject } from "@/hooks/use-project";
import { client } from "@/hono";
import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import type { ResourceLinkListItem } from "@msuite/utils/types";
import React from "react";

/** Props Interface */
interface TicketFormResourceLinkItemProps {
	resourceLink: ResourceLinkListItem;
	onDeleted: () => Promise<void>;
}

export function TicketFormResourceLinkItem({
	resourceLink,
	onDeleted,
}: TicketFormResourceLinkItemProps) {
	/** Hooks */
	const { t } = useTranslation();
	const asyncModal = useAsyncModal();

	/** Query */
	const { project } = useProject({ projectId: resourceLink.projectId });

	/** Functions */
	async function handleOnDelete() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.projects[":projectId"]["resource-links"][
			":resourceLinkId"
		].$delete({
			param: {
				projectId: resourceLink.projectId,
				resourceLinkId: resourceLink.id,
			},
		});
		await responseHandler(response);
		await onDeleted();
	}

	/** Render */
	return (
		<React.Fragment>
			<div className="h-stack items-center gap-2">
				<div className="min-w-0 flex-1">
					<ResourceLinkCard
						url={resourceLink.link}
						name={resourceLink.name}
					/>
				</div>
				<Can
					action="update"
					subject="Project"
					resource={project ? { ...project } : undefined}
				>
					<Button
						size="xxs"
						variant="ghost"
						icon={<Icon name="Trash2" />}
						onClick={handleOnDelete}
						data-cy="ticket-resource-link-delete-button"
					/>
				</Can>
			</div>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("confirmation.confirm_deletion")}
				/>
			)}
		</React.Fragment>
	);
}
